export enum Currency {
  ARS = 'ARS', // Peso argentino
  BRL = 'BRL', // Real brasileño
  CLP = 'CLP', // Peso chileno
  COP = 'COP', // Peso colombiano
  EUR = 'EUR', // Euro
  MXN = 'MXN', // Peso mexicano
  PEN = 'PEN', // Sol peruano
  PYG = 'PYG', // Guaraní paraguayo
  USD = 'USD', // Dólar estadounidense
  UYU = 'UYU', // Peso uruguayo
}



export function getCurrencySymbol(currencyCode: Currency): string {
  const currencySymbols: { [key: string]: string } = {
      [Currency.USD]: 'U$S',
      [Currency.EUR]: '€',
      [Currency.UYU]: '$',
      [Currency.ARS]: '$',
      [Currency.BRL]: 'R$',
  };
  
  return currencySymbols[currencyCode] || currencyCode.toString();
} 


/**
 * Analiza un patrón de formato de precio y extrae la configuración necesaria
 * @param pattern - Patrón de ejemplo como '1,000.12', '1.000,12', '1000.12', '1000'
 * @returns Configuración de formato de precio para Intl.NumberFormat
 * 
 * Patrones soportados:
 * - '1.000,12' → es-ES (punto para miles, coma para decimales)
 * - '1,000.12' → en-US (coma para miles, punto para decimales)
 * - '1000,12'  → sin separador de miles, coma para decimales
 * - '1000.12'  → sin separador de miles, punto para decimales
 * - '1.000'    → punto para miles, sin decimales
 * - '1,000'    → coma para miles, sin decimales
 * - '1000'     → sin separadores, sin decimales
 */
export function parsePriceFormatPattern(pattern: string): {
  locale: string;
  useGrouping: boolean;
  minimumFractionDigits: number;
  maximumFractionDigits: number;
} {
  // Detectar separador de miles y decimales
  const hasComma = pattern.includes(',');
  const hasPeriod = pattern.includes('.');
  
  let locale = 'es-UY'; // Por defecto
  let useGrouping = false;
  let minimumFractionDigits = 0;
  let maximumFractionDigits = 0;
  
  // Caso 1: Tiene coma y punto → determinar cuál es miles y cuál decimales
  if (hasComma && hasPeriod) {
      const commaIndex = pattern.indexOf(',');
      const periodIndex = pattern.indexOf('.');
      
      if (commaIndex < periodIndex) {
          // '1,000.12' → coma es miles, punto es decimales (formato en-US)
          locale = 'en-US';
          useGrouping = true;
          const decimalPart = pattern.split('.')[1];
          minimumFractionDigits = decimalPart ? decimalPart.length : 0;
          maximumFractionDigits = decimalPart ? decimalPart.length : 2;
      } else {
          // '1.000,12' → punto es miles, coma es decimales (formato es-ES)
          locale = 'es-ES';
          useGrouping = true;
          const decimalPart = pattern.split(',')[1];
          minimumFractionDigits = decimalPart ? decimalPart.length : 0;
          maximumFractionDigits = decimalPart ? decimalPart.length : 2;
      }
  }
  // Caso 2: Solo tiene coma
  else if (hasComma && !hasPeriod) {
      const parts = pattern.split(',');
      const integerPart = parts[0];
      const decimalPart = parts[1];
      
      // Si la parte después de la coma tiene más de 2 dígitos, es separador de miles
      // Ejemplo: '1,000' → miles
      // Ejemplo: '1000,12' → decimales
      if (decimalPart && decimalPart.length <= 2 && integerPart.length <= 4) {
          // Es separador decimal
          locale = 'es-ES';
          useGrouping = false;
          minimumFractionDigits = decimalPart.length;
          maximumFractionDigits = decimalPart.length;
      } else if (decimalPart && decimalPart.length === 3) {
          // Es separador de miles
          locale = 'en-US';
          useGrouping = true;
          minimumFractionDigits = 0;
          maximumFractionDigits = 0;
      } else if (!decimalPart) {
          // Solo una coma sin parte decimal, asumir formato internacional con miles
          locale = 'en-US';
          useGrouping = true;
          minimumFractionDigits = 0;
          maximumFractionDigits = 0;
      }
  }
  // Caso 3: Solo tiene punto
  else if (hasPeriod && !hasComma) {
      const parts = pattern.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];
      
      // Si la parte después del punto tiene más de 2 dígitos, es separador de miles
      // Ejemplo: '1.000' → miles
      // Ejemplo: '1000.12' → decimales
      if (decimalPart && decimalPart.length <= 2 && integerPart.length <= 4) {
          // Es separador decimal
          locale = 'en-US';
          useGrouping = false;
          minimumFractionDigits = decimalPart.length;
          maximumFractionDigits = decimalPart.length;
      } else if (decimalPart && decimalPart.length === 3) {
          // Es separador de miles
          locale = 'es-ES';
          useGrouping = true;
          minimumFractionDigits = 0;
          maximumFractionDigits = 0;
      } else if (!decimalPart) {
          // Solo un punto sin parte decimal
          locale = 'es-ES';
          useGrouping = true;
          minimumFractionDigits = 0;
          maximumFractionDigits = 0;
      }
  }
  // Caso 4: Sin separadores
  else {
      // '1000' → sin separadores
      locale = 'es-UY';
      useGrouping = false;
      minimumFractionDigits = 0;
      maximumFractionDigits = 0;
  }
  
  return {
      locale,
      useGrouping,
      minimumFractionDigits,
      maximumFractionDigits
  };
} 


